import {useMutation, UseMutationOptions, useQueryClient,} from '@tanstack/react-query';
import {Campaign, ReadBlockVariant, ReadPage, UpdateBlockVariant} from '@tinct/core-api-models';
import {ApiError, tinctApi} from '../../api';

export interface PatchBlockVariantParams {
  pageId: string;
  blockVariantId: string;
  data: Partial<UpdateBlockVariant>;
}

// Writing a variant can outdate the page's live version. The server decides
// that (CorePageService#markOutdatedForRepublication) and reports the outcome
// on the write response, so ask for the page's resulting `outdatedStatus`
// rather than re-reading the page or restating the rule client-side.
const patchResponseFields = {
  id: null,
  value: null,
  valueAsText: null,
  status: null,
  cssSelectors: null,
  sectionCssSelector: null,
  label: null,
  companies: null,
  updatedAt: null,
  page: {
    fields: {
      id: null,
      outdatedStatus: null,
    },
  },
};

export const patchBlockVariant = async (
  params: PatchBlockVariantParams,
): Promise<ReadBlockVariant> => {
  return await tinctApi.pages.patchBlockVariant(
    params.pageId,
    params.blockVariantId,
    params.data,
    patchResponseFields,
  );
};

export const usePatchBlockVariant = (
  options?: Omit<
    UseMutationOptions<ReadBlockVariant, ApiError, PatchBlockVariantParams>,
    'mutationFn'
  >,
) => {
  const { onSuccess, ...restOptions } = options || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: patchBlockVariant,
    onSuccess: (...args) => {
      const [updatedVariant, variables] = args;

      // Update the block variant directly in the cached page data instead of
      // invalidating the query (which triggers a refetch and an expensive
      // preview re-render). The `['pages', id]` prefix also matches the
      // chat-message queries, hence the `blockVariants` guard below.
      queryClient.setQueriesData<ReadPage>(
        { queryKey: ['pages', variables.pageId] },
        (oldData) => {
          if (!oldData?.blockVariants) return oldData;

          return {
            ...oldData,
            blockVariants: oldData.blockVariants.map((variant) => {
              if (variant.id !== variables.blockVariantId) return variant;

              return {
                ...variant,
                value: updatedVariant.value,
                valueAsText: updatedVariant.valueAsText,
                status: updatedVariant.status,
                cssSelectors: updatedVariant.cssSelectors,
                sectionCssSelector: updatedVariant.sectionCssSelector,
                label: updatedVariant.label,
                companies: updatedVariant.companies,
                updatedAt: updatedVariant.updatedAt,
              };
            }),
          };
        },
      );

      // Carry the page status the server just reported (see
      // `patchResponseFields`) into the campaign query, which is what the
      // outdated badges and the published-edit notice render from — the variant
      // patch above only touches the per-page query. Skipped when the response
      // carried no page, so a projection change can't blank the badges.
      const outdatedStatus = updatedVariant.page?.outdatedStatus;

      if (outdatedStatus !== undefined) {
        queryClient.setQueriesData<Campaign>({ queryKey: ['campaign'] }, (oldData) => {
          if (!oldData?.pages) return oldData;

          let changed = false;
          const pages = oldData.pages.map((page) => {
            if (page.id !== variables.pageId || page.outdatedStatus === outdatedStatus) {
              return page;
            }

            changed = true;
            return { ...page, outdatedStatus };
          });

          return changed ? { ...oldData, pages } : oldData;
        });
      }

      onSuccess?.(...args);
    },
    ...restOptions,
  });
};
